import {
  Box, Card, CardContent, Typography, TextField, Button, Divider,
  Alert, CircularProgress, Avatar, Tabs, Tab,
} from '@mui/material';
import { Person, Lock } from '@mui/icons-material';
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

function ProfileTab({ user }) {
  const dispatch = useDispatch();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    try {
      await api.patch('/auth/profile', { name, email });
      dispatch(showSnackbar({ message: 'Profile updated successfully', severity: 'success' }));
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Update failed', severity: 'error' }));
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 480 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: 28 }}>
          {name.charAt(0).toUpperCase()}
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight={700}>{name}</Typography>
          <Typography variant="body2" color="text.secondary">{user?.role}</Typography>
        </Box>
      </Box>
      <TextField label="Full Name" value={name} onChange={e => setName(e.target.value)} fullWidth />
      <TextField label="Email Address" value={email} onChange={e => setEmail(e.target.value)} fullWidth type="email" />
      <Button variant="contained" onClick={save} disabled={loading} sx={{ alignSelf: 'flex-start' }}>
        {loading ? <CircularProgress size={20} /> : 'Save Changes'}
      </Button>
    </Box>
  );
}

function PasswordTab() {
  const dispatch = useDispatch();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setError('');
    if (form.new_password !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.new_password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await api.patch('/auth/change-password', { current_password: form.current_password, new_password: form.new_password });
      dispatch(showSnackbar({ message: 'Password changed. Please login again.', severity: 'success' }));
      setForm({ current_password: '', new_password: '', confirm: '' });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to change password');
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 480 }}>
      {error && <Alert severity="error">{error}</Alert>}
      <TextField label="Current Password" type="password" value={form.current_password} onChange={set('current_password')} fullWidth />
      <TextField
        label="New Password" type="password" value={form.new_password} onChange={set('new_password')} fullWidth
        helperText="Min 8 chars, at least one uppercase letter and one number"
      />
      <TextField label="Confirm New Password" type="password" value={form.confirm} onChange={set('confirm')} fullWidth />
      <Button variant="contained" color="warning" onClick={save} disabled={loading} sx={{ alignSelf: 'flex-start' }}>
        {loading ? <CircularProgress size={20} /> : 'Change Password'}
      </Button>
    </Box>
  );
}

export default function ProfilePage() {
  const { user } = useSelector(s => s.auth);
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>My Profile</Typography>
      <Card>
        <CardContent>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
            <Tab icon={<Person />} label="Profile Info" iconPosition="start" />
            <Tab icon={<Lock />} label="Change Password" iconPosition="start" />
          </Tabs>
          {tab === 0 ? <ProfileTab user={user} /> : <PasswordTab />}
        </CardContent>
      </Card>
    </Box>
  );
}
